// 레스토랑 정보 섹션 컴포넌트
import { MapPin, Phone } from 'lucide-react';
import { receiptTranslations } from './constants';
import { useApp } from '@/contexts/AppContext';

interface RestaurantInfoProps {
  /** 실제 매장명 — 없으면 번역 상수 폴백(#284) */
  restaurantName?: string;
  /** 실제 매장 주소 (#632) — 없으면 주소 줄 숨김 (더미 주소 노출 금지) */
  address?: string | null;
  /** 실제 매장 전화번호 (#632) — 없으면 전화 줄 숨김 (더미 번호 노출 금지) */
  phone?: string | null;
}

export function RestaurantInfo({ restaurantName, address, phone }: RestaurantInfoProps = {}) {
  // #522: 15 locale 전수 lookup — Locale 단일 출처 정합.
  const { locale } = useApp();
  const t = receiptTranslations[locale];
  const displayName = restaurantName?.trim() || t.restaurantName;
  // #632: 더미 'Calle Table Order 123' 폴백 제거 — 실데이터 없으면 해당 줄 미표시.
  // typeof 가드: API 런타임 데이터가 string 이 아닌 경우(비정상 응답) 줄 숨김 (defense in depth)
  const displayAddress = (typeof address === 'string' && address.trim()) || null;
  const displayPhone = (typeof phone === 'string' && phone.trim()) || null;

  return (
    <div className="flex items-start gap-3 px-5 py-5 border-b-2 border-border pt-[0px] pr-[20px] pb-[20px] pl-[20px]">
      {/* 로고 */}
      <div className="w-14 h-14 bg-muted rounded-[5px] overflow-hidden shrink-0">
        <img
          src="https://images.unsplash.com/photo-1758448500813-59f3770da00d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyZXN0YXVyYW50JTIwaW50ZXJpb3IlMjBtb2Rlcm4lMjBkaW5pbmd8ZW58MXx8fHwxNzU5MDU1MzYyfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
          alt="Restaurant interior"
          className="w-full h-full object-cover"
        />
      </div>

      {/* 레스토랑 정보 */}
      <div className="flex-1 min-w-0">
        <h2 className="text-base font-bold text-foreground mb-2.5">
          {displayName}
        </h2>

        {(displayAddress || displayPhone) && (
          <div className="space-y-1.5">
            {displayAddress && (
              <div className="flex items-start gap-2" data-testid="restaurant-address">
                <MapPin className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {displayAddress}
                </p>
              </div>
            )}

            {displayPhone && (
              <div className="flex items-center gap-2" data-testid="restaurant-phone">
                <Phone className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <p className="text-xs text-muted-foreground">{displayPhone}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
